import { useEffect, useRef, useState } from "react";
import { Share2, Check, Link as LinkIcon, Mail, MessageCircle, Instagram } from "lucide-react";

/*
 * Simple share menu with WhatsApp, Email, Instagram (copies link since
 * Instagram has no public share URL) and Copy link. Uses the native
 * Web Share API on mobile if available.
 */
export const ShareMenu = ({ title, text, url, testIdPrefix = "share" }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const href = url || (typeof window !== "undefined" ? window.location.href : "");
  const encoded = encodeURIComponent(`${text ? text + ", " : ""}${href}`);

  const trigger = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: href });
        return;
      } catch {
        /* user cancelled, fall through to menu */
      }
    }
    setOpen((o) => !o);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard denied, silent */
    }
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={trigger}
        data-testid={`${testIdPrefix}-button`}
        className="inline-flex items-center gap-2 rounded-full btn-ghost font-semibold px-5 py-2.5"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Share2 size={16} strokeWidth={1.8} />
        Share
      </button>

      {open && (
        <div
          role="menu"
          data-testid={`${testIdPrefix}-menu`}
          className="absolute right-0 mt-2 w-60 rounded-2xl overflow-hidden z-40 shadow-lg"
          style={{
            background: "var(--brand-cream)",
            border: "1px solid var(--brand-line)",
          }}
        >
          <a
            href={`https://wa.me/?text=${encoded}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            data-testid={`${testIdPrefix}-whatsapp`}
            className="flex items-center gap-3 px-4 py-3 text-sm font-semibold hover:opacity-70"
            style={{ color: "var(--brand-ink)" }}
          >
            <MessageCircle size={16} strokeWidth={1.8} style={{ color: "#25D366" }} />
            Share on WhatsApp
          </a>
          <a
            href={`mailto:?subject=${encodeURIComponent(title || "")}&body=${encoded}`}
            onClick={() => setOpen(false)}
            data-testid={`${testIdPrefix}-email`}
            className="flex items-center gap-3 px-4 py-3 text-sm font-semibold hover:opacity-70"
            style={{
              color: "var(--brand-ink)",
              borderTop: "1px solid var(--brand-line)",
            }}
          >
            <Mail size={16} strokeWidth={1.8} style={{ color: "var(--brand-sage)" }} />
            Share via email
          </a>
          <button
            onClick={async () => {
              await copy();
              window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
              setOpen(false);
            }}
            data-testid={`${testIdPrefix}-instagram`}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-left hover:opacity-70"
            style={{
              color: "var(--brand-ink)",
              borderTop: "1px solid var(--brand-line)",
            }}
          >
            <Instagram size={16} strokeWidth={1.8} style={{ color: "var(--brand-accent)" }} />
            Share on Instagram
          </button>
          <button
            onClick={copy}
            data-testid={`${testIdPrefix}-copy`}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-left hover:opacity-70"
            style={{
              color: "var(--brand-ink)",
              borderTop: "1px solid var(--brand-line)",
            }}
          >
            {copied ? (
              <Check size={16} strokeWidth={2} style={{ color: "var(--brand-sage)" }} />
            ) : (
              <LinkIcon size={16} strokeWidth={1.8} style={{ color: "var(--brand-muted)" }} />
            )}
            {copied ? "Link copied" : "Copy link"}
          </button>
        </div>
      )}
    </div>
  );
};
